import React, { useCallback } from 'react';
import { clubAnnouncementService } from '@/services/api/club-announcements.service';

/**
 * Wraps a message row. When announcementId is provided, fires recordAnnouncementView
 * once when the card is 50% visible, then disconnects. Session-level dedup via
 * viewedRef prevents repeat calls; DB unique index deduplicates cross-session.
 * When announcementId is undefined, renders a plain div with no observer.
 */
export const AnnouncementObserverRow = React.memo(function AnnouncementObserverRow({
  announcementId,
  viewedRef,
  className,
  style,
  children,
}: {
  announcementId?: string;
  viewedRef: React.MutableRefObject<Set<string>>;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !announcementId || viewedRef.current.has(announcementId)) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            observer.disconnect();
            viewedRef.current.add(announcementId);
            clubAnnouncementService.recordAnnouncementView(announcementId);
          }
        },
        { threshold: 0.5 }
      );
      observer.observe(node);
    },
    [announcementId, viewedRef]
  );

  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
});
